import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader } from 'lucide-react';
import { paymentAPI } from '../services/api';

const PremiumSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const verifyPayment = async () => {
      const provider = searchParams.get('provider');
      
      // Handle PayPal payment
      if (provider === 'paypal') {
        const paymentId = searchParams.get('paymentId');
        const payerId = searchParams.get('PayerID');
        const planId = searchParams.get('plan_id');
        const userId = searchParams.get('user_id');
        
        if (!paymentId || !payerId) {
          setStatus('error');
          return;
        }
        
        try {
          await paymentAPI.executePayPalPayment(paymentId, payerId, planId, userId);
          setStatus('success');
          
          // Update user in localStorage
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          user.isPremium = true;
          localStorage.setItem('user', JSON.stringify(user));
          
          // Reload to update UI
          setTimeout(() => {
            window.location.href = '/';
          }, 3000);
        } catch (error) {
          setStatus('error');
        }
        return;
      }
      
      // Handle Stripe payment
      if (!sessionId) {
        setStatus('error');
        return;
      }

      try {
        const response = await paymentAPI.getSessionStatus(sessionId);
        
        if (response.payment_status === 'paid') {
          setStatus('success');
          
          // Update user in localStorage
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          user.isPremium = true;
          localStorage.setItem('user', JSON.stringify(user));
          
          // Reload to update UI
          setTimeout(() => {
            window.location.href = '/';
          }, 3000);
        } else {
          setStatus('pending');
        }
      } catch (error) {
        setStatus('error');
      }
    };

    verifyPayment();
  }, [sessionId, searchParams]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center px-4">
        <Card className="max-w-md w-full p-8 text-center">
          <Loader className="w-16 h-16 text-green-600 animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Verifying Payment...</h2>
          <p className="text-gray-600">Please wait while we confirm your subscription.</p>
        </Card>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center px-4">
        <Card className="max-w-md w-full p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Welcome to Premium! 🎉</h2>
          <p className="text-gray-700 mb-6">
            Your subscription has been activated successfully. You now have access to:
          </p>
          <ul className="text-left space-y-2 mb-6 text-gray-700">
            <li className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              <span>Self-coaching tools & resources</span>
            </li>
            <li className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              <span>AI-powered coaching</span>
            </li>
            <li className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              <span>Unlimited assessment history</span>
            </li>
            <li className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              <span>Priority support</span>
            </li>
          </ul>
          <p className="text-sm text-gray-600 mb-6">Redirecting to home page...</p>
          <Button 
            onClick={() => navigate('/')}
            className="w-full bg-green-600 hover:bg-green-700 text-white"
          >
            Go to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center px-4">
      <Card className="max-w-md w-full p-8 text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-3xl">⚠️</span>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Payment Verification Failed</h2>
        <p className="text-gray-700 mb-6">
          We couldn't verify your payment. Please contact support if you believe this is an error.
        </p>
        <Button 
          onClick={() => navigate('/premium')}
          className="w-full bg-green-600 hover:bg-green-700 text-white"
        >
          Back to Premium
        </Button>
      </Card>
    </div>
  );
};

export default PremiumSuccessPage;
